import { HOMEWORK_DEFAULT_TITLE_KEYS } from '@cc/schema';
import { useCustom, useTranslate } from '@refinedev/core';
import { Button, Space, Table, Tag, Typography } from 'antd';
import { useMemo, useState } from 'react';
import {
  ResultCard,
  ToolAssignButton,
  ToolSection,
  fmtDayShort,
  fmtDayTimeShort,
  fmtWeekday,
} from '../../components/ToolSection';
import type { AssignPreset } from '../homework/model';
import { BaPlanDrawer } from './BaPlanDrawer';
import {
  BA_STEPS,
  LADDER_FIELDS,
  WEEK_FIELDS,
  addDays,
  dayOf,
  mondayOf,
  weekIsEmpty,
  type LadderRow,
  type PlanItem,
  type WeekRow,
} from './model';

/**
 * E41 · ActivityWeek (portal) + PlanActivity (in session) + AssignHomework (BA step) — "Client Hub — Behavioral activation": the client's weekly
 * review, the SAME view the client has on the phone — planned vs done, expected vs actual Enjoyment and Importance, hours per life
 * area, mood beside the activities done, the commonest barrier, and (only when the client shares activity with THIS therapist) the
 * phone's own count — with the monitoring grid behind it (the diary of hours) and the ladder. The week navigation lives in the toolbar. The
 * therapist plans WITH the client from a side drawer on the Plans card (the same PlanActivity, performer the therapist: the plan lands on the
 * client's phone and Homework list) and assigns a BA step with the ONE Assign button (monitoring defaults to daily at 21:00 for 7 days;
 * the other three steps are due-optional) — each completing on the client's OWN entry.
 *
 * Design PORTAL-SECTIONS-2026-10-22 §2.5. Visible through a relation with VIEW permission; planning in session needs a granted relation
 * (the server's E34 verdict). NOTHING AUTOMATIC: no alert, score or notification is derived from a rating, a plan or a miss. Numbers only.
 */
type Props = { clientId: string; clientName: string; locale: 'en' | 'ru' };

const num = (n: number | null | undefined) => (n == null ? '–' : String(n));

export const ClientActivateTab = ({ clientId, clientName, locale }: Props) => {
  const t = useTranslate() as unknown as (
    k: string,
    o?: Record<string, unknown>,
  ) => string;

  const [monday, setMonday] = useState(() => mondayOf(new Date()));
  const [planOpen, setPlanOpen] = useState(false);
  const week = useCustom<WeekRow>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'activityWeek',
      fields: WEEK_FIELDS,
      variables: {
        subject: { name: 'subject', type: 'ID', value: clientId },
        week: { name: 'week', type: 'String', value: dayOf(monday) },
        timeZone: {
          name: 'timeZone',
          type: 'String',
          value: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      },
    },
    queryOptions: { enabled: Boolean(clientId), retry: false },
  });
  const ladderQuery = useCustom<LadderRow>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'activityLadder',
      fields: LADDER_FIELDS,
      variables: { subject: { name: 'subject', type: 'ID', value: clientId } },
    },
    queryOptions: { enabled: Boolean(clientId), retry: false },
  });

  const view = week.data?.data;
  const ladder = ladderQuery.data?.data;
  const when = (iso: string) => fmtDayTimeShort(iso, locale);
  const dayLabel = (iso: string) => fmtWeekday(iso, locale);

  // ── the one Assign: monitoring daily 21:00 for 7 days; the other three steps due-optional ─────
  const assignPreset: AssignPreset = useMemo(
    () => ({
      kind: 'tool',
      target: 'activityMonitoring',
      targets: BA_STEPS,
      defaults: Object.fromEntries(
        BA_STEPS.map((s) => [
          s,
          s === 'activityMonitoring'
            ? {
                title: t(HOMEWORK_DEFAULT_TITLE_KEYS[s]), // Q-ru-3 A: left as it is, no title is stored
                reminderPolicy: 'default' as const,
                deadline: {
                  shape: 'repeat' as const,
                  frequency: 'daily' as const,
                  hour: 21,
                  minute: 0,
                  startInDays: 1,
                  count: 7,
                  countMax: 28,
                  countUnit: 'days' as const,
                },
              }
            : {
                title: t(HOMEWORK_DEFAULT_TITLE_KEYS[s]), // Q-ru-3 A: left as it is, no title is stored
                reminderPolicy: 'default' as const,
                deadline: { shape: 'due' as const, optional: true },
              },
        ]),
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locale],
  );

  const planColumns = [
    {
      title: t('ba.hub.plans.when'),
      render: (_: unknown, p: PlanItem) => when(p.plannedFor),
    },
    { title: t('ba.hub.plans.what'), dataIndex: 'title' },
    {
      title: t('ba.hub.plans.expected'),
      render: (_: unknown, p: PlanItem) =>
        t('ba.hub.ratings', {
          enjoyment: p.predictedEnjoyment,
          importance: p.predictedImportance,
        }),
    },
    {
      title: t('ba.hub.plans.actual'),
      render: (_: unknown, p: PlanItem) =>
        p.status === 'done'
          ? t('ba.hub.ratings', {
              enjoyment: num(p.enjoyment),
              importance: num(p.importance),
            })
          : '–',
    },
    {
      title: t('ba.hub.plans.status'),
      render: (_: unknown, p: PlanItem) => (
        <Space size={4} wrap>
          <Tag>{t(`ba.agenda.status.${p.status}`)}</Tag>
          {p.barrier ? <span>{t(`ba.barrier.${p.barrier}`)}</span> : null}
          {p.plannedBy ? (
            <Tag color="blue">{t('ba.hub.plans.inSession')}</Tag>
          ) : null}
        </Space>
      ),
    },
  ];

  const nothing = view ? weekIsEmpty(view) : false;
  const plans = view?.plans ?? [];
  const hours = view?.hours ?? [];

  const toolbar = (
    <>
      <Button
        onClick={() => setMonday(addDays(monday, -7))}
        data-testid="activate-hub-prev"
      >
        {t('ba.agenda.prev')}
      </Button>
      <span data-testid="activate-hub-week-label">
        {t('ba.agenda.weekOf', { date: fmtDayShort(monday, locale) })}
      </span>
      <Button
        onClick={() => setMonday(addDays(monday, 7))}
        data-testid="activate-hub-next"
      >
        {t('ba.agenda.next')}
      </Button>
    </>
  );

  return (
    <ToolSection
      testId="activate-hub"
      title={t('ba.hub.title')}
      purpose={t('ba.hub.purpose')}
      actions={
        <ToolAssignButton
          clientId={clientId}
          clientName={clientName}
          preset={assignPreset}
          onAssigned={() => {
            week.refetch();
          }}
        />
      }
      toolbar={toolbar}
      loadError={week.isError ? t('ba.hub.loadError') : null}
      loadErrorTestId="activate-hub-load-error"
    >
      <ResultCard
        testId="activate-hub-week"
        role="result"
        title={t('ba.hub.week.title')}
      >
        {view ? (
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <span data-testid="activate-hub-counts">
              {t('ba.hub.week.counts', {
                planned: view.planned,
                done: view.done,
                missed: view.missed,
              })}
            </span>
            {nothing ? (
              <Typography.Text
                type="secondary"
                data-testid="activate-hub-nothing"
              >
                {t('ba.hub.week.nothing')}
              </Typography.Text>
            ) : (
              <>
                <span data-testid="activate-hub-predicted">
                  {t('ba.week.enjoyment', {
                    predicted: num(view.predictedVsActual.enjoyment.predicted),
                    actual: num(view.predictedVsActual.enjoyment.actual),
                  })}
                  {' · '}
                  {t('ba.week.importance', {
                    predicted: num(view.predictedVsActual.importance.predicted),
                    actual: num(view.predictedVsActual.importance.actual),
                  })}
                </span>
                {view.topBarrier ? (
                  <span data-testid="activate-hub-barrier">
                    {t('ba.week.barrier', {
                      barrier: t(`ba.barrier.${view.topBarrier}`),
                    })}
                  </span>
                ) : null}
                <span data-testid="activate-hub-areas">
                  {view.byLifeArea.length
                    ? view.byLifeArea
                        .map(
                          (a) =>
                            `${t(`ba.areas.${a.area}`)} ${t('ba.week.hours', { n: a.hours })}`,
                        )
                        .join(' · ')
                    : t('ba.hub.week.noAreas')}
                </span>
                {view.emptyAreas.length ? (
                  <Typography.Text
                    type="secondary"
                    data-testid="activate-hub-empty-areas"
                  >
                    {t('ba.week.emptyAreas', {
                      areas: view.emptyAreas
                        .map((a) => t(`ba.areas.${a}`))
                        .join(', '),
                    })}
                  </Typography.Text>
                ) : null}
                <Table
                  size="small"
                  pagination={false}
                  rowKey="date"
                  dataSource={view.moodByDay}
                  scroll={{ x: 'max-content' }}
                  onRow={(r) =>
                    ({ 'data-testid': `activate-hub-mood-${r.date}` }) as never
                  }
                  columns={[
                    {
                      title: t('ba.hub.week.day'),
                      render: (
                        _: unknown,
                        r: WeekRow['moodByDay'][number],
                      ) => dayLabel(r.date),
                    },
                    {
                      title: t('ba.week.mood'),
                      render: (
                        _: unknown,
                        r: WeekRow['moodByDay'][number],
                      ) => (r.mood === null ? '–' : r.mood),
                    },
                    {
                      title: t('ba.hub.week.activities'),
                      dataIndex: 'activitiesDone',
                    },
                    ...(view.healthActivity
                      ? [
                          {
                            title: t('ba.hub.week.phone'),
                            render: (
                              _: unknown,
                              r: WeekRow['moodByDay'][number],
                            ) =>
                              num(
                                view.activityDays.find((d) => d.date === r.date)
                                  ?.activeMinutes,
                              ),
                          },
                        ]
                      : []),
                  ]}
                />
              </>
            )}
            {view.healthActivity ? (
              <span data-testid="activate-hub-health">
                {t('ba.week.healthLine', {
                  days: view.healthActivity.activeDays,
                  minutes: view.healthActivity.activeMinutes,
                  walks: view.healthActivity.walks,
                })}
              </span>
            ) : null}
          </Space>
        ) : null}
      </ResultCard>

      <ResultCard
        testId="activate-hub-plans"
        role="result"
        title={t('ba.hub.plans.title')}
        actions={
          <Button
            onClick={() => setPlanOpen(true)}
            data-testid="activate-hub-plan-open"
          >
            {t('ba.hub.plan.open')}
          </Button>
        }
      >
        {plans.length ? (
          <Table<PlanItem>
            size="small"
            pagination={false}
            rowKey="plannedActivityId"
            dataSource={plans}
            columns={planColumns}
            scroll={{ x: 'max-content' }}
            onRow={(r) =>
              ({
                'data-testid': `activate-hub-plan-${r.plannedActivityId}`,
              }) as never
            }
          />
        ) : (
          <Typography.Text type="secondary">
            {t('ba.hub.plans.empty')}
          </Typography.Text>
        )}
      </ResultCard>

      <ResultCard
        testId="activate-hub-grid"
        role="result"
        title={t('ba.hub.grid.title')}
      >
        {hours.length ? (
          <Table
            size="small"
            pagination={false}
            rowKey="activityLogId"
            dataSource={hours}
            scroll={{ x: 'max-content' }}
            onRow={(r) =>
              ({
                'data-testid': `activate-hub-hour-${r.activityLogId}`,
              }) as never
            }
            columns={[
              {
                title: t('ba.hub.plans.when'),
                render: (_: unknown, h: WeekRow['hours'][number]) =>
                  when(h.startAt),
              },
              { title: t('ba.hub.plans.what'), dataIndex: 'title' },
              { title: t('ba.log.enjoyment'), dataIndex: 'enjoyment' },
              { title: t('ba.log.importance'), dataIndex: 'importance' },
              {
                title: t('ba.hub.grid.area'),
                render: (_: unknown, h: WeekRow['hours'][number]) =>
                  h.area ? t(`ba.areas.${h.area}`) : '–',
              },
            ]}
          />
        ) : (
          <Typography.Text type="secondary">
            {t('ba.hub.grid.empty')}
          </Typography.Text>
        )}
      </ResultCard>

      <ResultCard
        testId="activate-hub-ladder"
        role="result"
        title={t('ba.hub.ladder.title')}
      >
        {ladder && ladder.rungs.length ? (
          <Space direction="vertical" size={2}>
            {[...ladder.rungs].reverse().map((r) => (
              <span
                key={r.valueActivityId}
                data-testid={`activate-hub-rung-${r.rank}`}
              >
                {r.rank}. {r.title} ·{' '}
                {t('ba.choose.ideas.difficulty', { n: r.difficulty })}
              </span>
            ))}
          </Space>
        ) : (
          <Typography.Text
            type="secondary"
            data-testid="activate-hub-ladder-empty"
          >
            {t('ba.hub.ladder.empty')}
          </Typography.Text>
        )}
      </ResultCard>

      {planOpen ? (
        <BaPlanDrawer
          clientId={clientId}
          rungs={ladder?.rungs ?? []}
          onClose={() => setPlanOpen(false)}
          onSaved={(plannedFor) => {
            // the cards jump to the plan's week (a same-week plan just re-reads)
            const target = mondayOf(plannedFor);
            if (dayOf(target) === dayOf(monday)) week.refetch();
            else setMonday(target);
          }}
        />
      ) : null}
    </ToolSection>
  );
};
